import { Injectable } from '@angular/core';
import { LabelNameProjector } from '@protocols/domain';
import { from, Observable } from 'rxjs';
import { filter, map, mergeMap, pairwise, startWith, withLatestFrom } from 'rxjs/operators';
import { useLocalization } from '@shared/localization';

import { LabelConfigState } from '../state/label-config.state';
import { LabelSaveFailure, LabelState } from '../state/label.state';

/**
 * A row failure surfaced by the label / label-config phases of the save
 * cascade, ready for a snackbar: `name` is the label's display name resolved
 * in the current app language (empty when no staging slot knows the uuid).
 */
export interface LabelFailureEvent {
  name: string;
  error: Error;
}

/**
 * Shared per-row failure EVENT streams for the save cascade's label batch and
 * label-config tail, consumed by every cascade-triggering editor facade
 * (pipeline, method-type, case-type).
 *
 * Deliberately holds NO `effect()` calls: its job is pure cross-state
 * derivation — turning the owner states' `saveExceptions` MAPS back into
 * per-row events, joined with `LabelState.labelLanguagesByUuid$` and the
 * current app language into a display name. That derivation cannot live on a
 * single State (it spans two states, the localization provider and a domain
 * rule), so per the UI-layer rules the Effects tier is its home; it is not a
 * passthrough — none of these streams exist on any state's surface.
 */
@Injectable()
export class LabelConsolidationFailureEffects {
  private readonly currentLanguage$: Observable<string>;

  /** Each failing label-save row emits one `{ name, error }` to toast. */
  public readonly labelSaveFailed$: Observable<LabelFailureEvent>;

  /** Per-row label-config tail failures — see `labelSaveFailed$`. */
  public readonly labelConfigSaveFailed$: Observable<LabelFailureEvent>;

  constructor(private readonly labelState: LabelState, private readonly labelConfigState: LabelConfigState) {
    this.currentLanguage$ = useLocalization().currentLanguage$;
    this.labelSaveFailed$ = this.toNamedFailures(this.labelState.saveFailures$);
    this.labelConfigSaveFailed$ = this.toNamedFailures(this.labelConfigState.saveFailures$);
  }

  /*
   * Emit one failure per row that newly appears in (or whose error changes
   * within) the owner state's failure set — the cascade clears that set at the
   * start of each attempt, so a fresh failure is always a new row. Turns error
   * STATE back into per-row EVENTS a component can toast.
   *
   * `rowId` is compared, never parsed: it is whatever opaque identity the owning
   * state mints per failing row.
   */
  private newFailures$(source$: Observable<LabelSaveFailure[]>): Observable<LabelSaveFailure> {
    return source$.pipe(
      startWith([] as LabelSaveFailure[]),
      pairwise(),
      map(([prev, curr]) => {
        const previousErrorByRow = new Map(prev.map((failure) => [failure.rowId, failure.error]));
        return curr.filter((failure) => previousErrorByRow.get(failure.rowId) !== failure.error);
      }),
      filter((added) => added.length > 0),
      mergeMap((added) => from(added))
    );
  }

  /*
   * Join the raw failures with the label lookup and the current app language into
   * displayable failures. A lookup miss resolves to `''` (the label never reached
   * any staging slot) — the consumer decides how to render a nameless failure.
   */
  private toNamedFailures(source$: Observable<LabelSaveFailure[]>): Observable<LabelFailureEvent> {
    return this.newFailures$(source$).pipe(
      withLatestFrom(this.labelState.labelLanguagesByUuid$, this.currentLanguage$),
      map(([{ labelUuid, error }, labelLanguagesByUuid, currentLanguage]) => ({
        name: LabelNameProjector.resolveDisplayName(labelLanguagesByUuid.get(labelUuid) ?? { en: '' }, currentLanguage),
        error,
      }))
    );
  }
}
